import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { IRedlineableField, JSONType } from "work/proposals/redlining/field-redlines/field-redline";

export default class Text implements IRedlineableField {
  public static fromJSON(value: unknown): Text | null | undefined {
    if (isNullOrUndefined(value)) return value;
    if (typeof value !== "string") throw new Error("Cannot create Text from non-string value");
    return new Text(value);
  }

  constructor(public readonly value: string) {}
  public get isEmpty(): boolean {
    return this.value === "";
  }
  public toJSON(): string {
    return this.value;
  }

  public toString(): string {
    return this.value;
  }

  public valueOf(): string {
    return this.value;
  }

  public isEqualTo(other: Text | null | undefined): boolean {
    return this.value === (other instanceof Text ? other?.value : other);
  }

  public clone(): Text {
    return this;
  }
}

function assertValidTextJSON(
  value: unknown,
  options?: { textTypeName: string; throwIfInvalid: boolean }
): value is null | undefined | string {
  const finalOptions = options ?? {
    textTypeName: Text.name,
    throwIfInvalid: true,
  };
  if (isNullOrUndefined(value) || typeof value === "string") return true;
  if (finalOptions.throwIfInvalid)
    throw new InvalidTextJSONError({
      json: value,
      message: `Cannot create ${finalOptions.textTypeName} from non-string value`,
    });
  return false;
}

export class InvalidTextJSONError extends Error {
  public readonly json: JSONType;
  public readonly message: string = `Cannot create Text from invalid value`;
  constructor(args: { json: JSONType; message?: string }) {
    super(`${args.message}: ${JSON.stringify(args.json)}`);
    this.json = args.json;
    if (args.message) this.message = args.message;
  }
}

export abstract class TextSubType extends Text {
  protected abstract get type(): typeof TextSubType;

  protected static fromTextTypeJSON<TextType extends Text>(
    value: string | null | undefined,
    options: {
      textType: new (value: string) => TextType;
    }
  ): TextType | null | undefined {
    return isNullOrUndefined(value) ? value : new options.textType(value);
  }

  protected static fromUnknownTextTypeJSON<TextType extends Text>(
    value: JSONType,
    options: {
      textType: new (value: string) => TextType;
      undefinedIfInvalid?: boolean;
    }
  ): Text | null | undefined {
    return assertValidTextJSON(value, {
      textTypeName: options.textType.name,
      throwIfInvalid: !options.undefinedIfInvalid,
    })
      ? TextSubType.fromTextTypeJSON(value, options)
      : undefined;
  }
}
